import { APEX_ORIGIN } from "@/lib/zones/app-routes";

/**
 * Cross-zone redirect hop budget.
 *
 * The apex worker and the `app` zone worker each answer "is this visitor
 * logged in?" differently — the apex decides at the edge from cookie
 * *presence* (`src/middleware.ts`, no D1 available there), the app zone
 * decides from a real D1-backed `getSafeSession()`. When the two answers
 * disagree for one session, their redirects point at each other and form a
 * cycle with no exit:
 *
 *   apex /{loc}/login  --(session valid → callbackURL)-->  apex /{loc}/settings
 *   apex /{loc}/settings  --(path regex, no session check)-->  app /{loc}/settings
 *   app  /{loc}/settings  --(session INVALID → /login)-->  app /{loc}/login
 *   app  /{loc}/login  --(not an app-zone route)-->  apex /{loc}/login  ↺
 *
 * Every hop is individually correct, which is why every single-hop probe and
 * every unit test passed while a real visitor got ERR_TOO_MANY_REDIRECTS
 * (2026-09-08T21:55Z, ~11 min after a `deploy-app-zone.yml` run — a separate
 * worker deploy is exactly what makes the two sides disagree for a window).
 * The only user-side exit was deleting cookies.
 *
 * A disagreement between two independently-deployed workers cannot be designed
 * away — they will always be able to skew. What CAN be removed is the
 * unboundedness: every cross-zone redirect stamps a hop count into the URL, and
 * once the budget is spent the next handler must RENDER something instead of
 * redirecting again. A disagreement then costs a bounded chain ending on the
 * login page — the honest answer when the validating side says "not signed in"
 * — instead of an infinite loop.
 *
 * Edge-safe by construction: pure string/URL work, no D1, no KV, no bindings.
 * Imported by both `src/middleware.ts` and `apps/app/src/middleware.ts`
 * (the app zone aliases `@/*` to this same `src/`).
 */

/**
 * Query parameter carrying the hop count. Underscore-prefixed to mark it as
 * machine plumbing rather than a product param, and stripped from anything
 * user- or crawler-facing (see `stripHop`).
 */
export const ZONE_HOP_PARAM = "_zh";

/**
 * Hops allowed before a handler must render instead of redirect.
 *
 * 2 is the smallest value that leaves every LEGITIMATE chain intact. The
 * longest correct chain in production is 2 stamped hops — e.g. apex `/login`
 * (post-sign-in) → apex `/{loc}/settings` → app `/{loc}/settings`, which then
 * renders. A third stamped hop means a handler is sending the visitor back
 * where they came from, which is the cycle and never a correct outcome.
 */
export const MAX_ZONE_HOPS = 2;

function toUrl(url: URL | string): URL | null {
	if (url instanceof URL) return url;
	try {
		// Relative paths are legal callers here (`buildLoginRedirect` returns
		// one); the base is irrelevant because only the query is ever read.
		return new URL(url, APEX_ORIGIN);
	} catch {
		return null;
	}
}

/** How many cross-zone hops this request has already cost. */
export function readHops(url: URL | string): number {
	const parsed = toUrl(url);
	if (!parsed) return 0;
	const raw = parsed.searchParams.get(ZONE_HOP_PARAM);
	if (raw === null) return 0;
	const hops = Number.parseInt(raw, 10);
	// A hostile or corrupted value must fail SAFE — treat anything
	// unparseable as "budget spent" so it terminates rather than loops.
	if (!Number.isFinite(hops) || hops < 0) return MAX_ZONE_HOPS;
	return hops;
}

/**
 * True when the next handler must render a page instead of redirecting again.
 */
export function hopsExhausted(url: URL | string): boolean {
	return readHops(url) >= MAX_ZONE_HOPS;
}

/**
 * `hopsExhausted` for a caller that already has the raw param rather than a
 * URL — a Next.js page reading `searchParams`, where the value arrives as
 * `string | string[] | undefined`. Fails safe the same way `readHops` does.
 */
export function hopsExhaustedFromValue(
	raw: string | string[] | undefined,
): boolean {
	const value = Array.isArray(raw) ? raw[0] : raw;
	// Absent is the only "false" case. A param that is PRESENT but empty is
	// malformed, and malformed fails safe — same rule as `readHops`, so a URL
	// and a page's `searchParams` never disagree about the same request.
	if (typeof value !== "string") return false;
	const hops = Number.parseInt(value, 10);
	if (!Number.isFinite(hops) || hops < 0) return true;
	return hops >= MAX_ZONE_HOPS;
}

/**
 * Same path with the hop count incremented. Call this on every cross-zone
 * redirect target — a redirect that forgets to stamp is invisible to the
 * budget and re-opens the loop.
 *
 * Accepts and returns the same shape it was given: a relative path stays
 * relative (so `buildLoginRedirect`'s output stays usable by `redirect()`),
 * an absolute URL stays absolute.
 */
export function withHop(url: string): string {
	const parsed = toUrl(url);
	if (!parsed) return url;
	parsed.searchParams.set(ZONE_HOP_PARAM, String(readHops(parsed) + 1));
	return url.startsWith("/")
		? `${parsed.pathname}${parsed.search}${parsed.hash}`
		: parsed.toString();
}

/**
 * Same URL with the hop plumbing removed. Use before anything a person or a
 * crawler sees — canonical/hreflang tags, sitemap entries, analytics path
 * templates — so a broken-loop URL never gets indexed or split into its own
 * analytics row.
 */
export function stripHop(url: string): string {
	const parsed = toUrl(url);
	if (!parsed || !parsed.searchParams.has(ZONE_HOP_PARAM)) return url;
	parsed.searchParams.delete(ZONE_HOP_PARAM);
	return url.startsWith("/")
		? `${parsed.pathname}${parsed.search}${parsed.hash}`
		: parsed.toString();
}
